/**
 * Tables for the MARS Performance section: the portfolio-level metrics
 * table and the per-asset-class breakdown table. Pure presentational
 * components — `PerformanceSection.tsx` composes them.
 */
import { ASSET_COLORS } from '@/lib/plotly/colors';
import { fmtPct, fmtRatio } from '../lib/utils';
import { ASSET_ORDER } from '../lib/performanceCharts';
import type { AssetClassPerformance, PerformancePeriodData } from '../types';

interface PerformanceTableProps {
  data: PerformancePeriodData | undefined;
  hasBenchmark: boolean;
}

export function PerformanceTable({ data, hasBenchmark }: PerformanceTableProps): JSX.Element {
  const cell = (value: string) => (
    <td className="px-3 py-2 text-sm text-[color:var(--text-dark)]">{value}</td>
  );

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
      <table className="w-full text-left font-[var(--font-body)]">
        <thead className="bg-[color:var(--light-bg)] border-b border-gray-200">
          <tr>
            <th className="px-3 py-2 text-xs font-semibold text-gray-600" />
            <th className="px-3 py-2 text-xs font-semibold text-gray-600">Period Return</th>
            <th className="px-3 py-2 text-xs font-semibold text-gray-600">Ann. Return</th>
            <th className="px-3 py-2 text-xs font-semibold text-gray-600">Ann. Volatility</th>
            <th className="px-3 py-2 text-xs font-semibold text-gray-600">Sharpe</th>
            <th className="px-3 py-2 text-xs font-semibold text-gray-600">Sortino</th>
            <th className="px-3 py-2 text-xs font-semibold text-gray-600">Max DD</th>
            {hasBenchmark ? (
              <>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600">Alpha</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600">Active Return</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600">Beta</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600">Correlation</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600">Tracking Error</th>
              </>
            ) : null}
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-gray-100">
            <td className="px-3 py-2 text-xs font-semibold text-gray-700">Portfolio</td>
            {cell(fmtPct(data?.period_return_pct))}
            {cell(fmtPct(data?.mean_return_pct))}
            {cell(fmtPct(data?.volatility_pct))}
            {cell(fmtRatio(data?.sharpe_ratio))}
            {cell(fmtRatio(data?.sortino_ratio))}
            {cell(fmtPct(data?.max_drawdown_pct))}
            {hasBenchmark ? (
              <>
                {cell(fmtPct(data?.alpha_pct))}
                {cell(fmtPct(data?.active_return_pct))}
                {cell(fmtRatio(data?.beta))}
                {cell(fmtRatio(data?.correlation))}
                {cell(fmtPct(data?.tracking_error_pct))}
              </>
            ) : null}
          </tr>
          {hasBenchmark ? (
            <tr>
              <td className="px-3 py-2 text-xs font-semibold text-gray-700">Benchmark</td>
              {cell(fmtPct(data?.benchmark_period_return_pct))}
              {cell(fmtPct(data?.benchmark_mean_return_pct))}
              {cell(fmtPct(data?.benchmark_volatility_pct))}
              {cell(fmtRatio(data?.benchmark_sharpe_ratio))}
              {cell(fmtRatio(data?.benchmark_sortino_ratio))}
              {cell(fmtPct(data?.benchmark_max_drawdown_pct))}
              <td colSpan={5} className="px-3 py-2 text-sm text-gray-400">
                -
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

interface AssetClassTableProps {
  assetClasses: Record<string, AssetClassPerformance | null>;
  hasBenchmark: boolean;
}

/**
 * Per-asset table showing Period Return, Active Return (colored
 * green/red), Alpha, Volatility, Sharpe, and Tracking Error.
 */
export function AssetClassTable({ assetClasses, hasBenchmark }: AssetClassTableProps): JSX.Element {
  const fmtActiveReturn = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '-';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto mb-4">
      <table className="w-full text-left font-[var(--font-body)]">
        <thead className="bg-[color:var(--light-bg)] border-b border-gray-200">
          <tr>
            <th className="px-3 py-2 text-xs font-semibold text-gray-600">Asset Class</th>
            <th className="px-3 py-2 text-xs font-semibold text-gray-600">Period Return</th>
            {hasBenchmark ? (
              <>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600">Active Return</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600">Alpha</th>
              </>
            ) : null}
            <th className="px-3 py-2 text-xs font-semibold text-gray-600">Volatility</th>
            <th className="px-3 py-2 text-xs font-semibold text-gray-600">Sharpe</th>
            {hasBenchmark ? (
              <th className="px-3 py-2 text-xs font-semibold text-gray-600">Tracking Error</th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {ASSET_ORDER.map((assetClass) => {
            const d = assetClasses[assetClass];
            if (!d) return null;
            const color = ASSET_COLORS[assetClass] ?? '#666';
            const activeReturn = d.active_return_pct;
            const activeColor = activeReturn != null && activeReturn >= 0 ? '#10B981' : '#EF4444';

            return (
              <tr key={assetClass} className="border-b border-gray-100">
                <td className="px-3 py-2 text-sm font-medium text-gray-900">
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      className="inline-block w-3 h-3 rounded-sm flex-shrink-0"
                      style={{ backgroundColor: color }}
                      aria-hidden
                    />
                    {assetClass}
                  </span>
                </td>
                <td className="px-3 py-2 text-sm text-gray-700">{fmtPct(d.period_return_pct)}</td>
                {hasBenchmark ? (
                  <>
                    <td className="px-3 py-2 text-sm font-medium" style={{ color: activeColor }}>
                      {fmtActiveReturn(activeReturn)}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-700">
                      {fmtActiveReturn(d.alpha_pct)}
                    </td>
                  </>
                ) : null}
                <td className="px-3 py-2 text-sm text-gray-700">{fmtPct(d.volatility_pct)}</td>
                <td className="px-3 py-2 text-sm text-gray-700">{fmtRatio(d.sharpe_ratio)}</td>
                {hasBenchmark ? (
                  <td className="px-3 py-2 text-sm text-gray-700">
                    {fmtPct(d.tracking_error_pct)}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
